import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import { Add, Groups, Sync, UploadFile } from '@mui/icons-material';
import { useNotification } from '../contexts/NotificationContext';
import { teamsApi, usersApi, InternalTeamEmail, InternalProfile } from '../services/api';
import { Team } from '../types';

const SIMPLE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const sortRosterEntries = (rows: InternalTeamEmail[]) =>
  [...rows].sort((a, b) => {
    const lastCompare = (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' });
    if (lastCompare !== 0) return lastCompare;
    const firstCompare = (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' });
    if (firstCompare !== 0) return firstCompare;
    return (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' });
  });

type UnresolvedReportSample = {
  id: string;
  filename: string;
  reason: string;
  parsed_name?: string | null;
  candidates?: string[];
  suggested_emails?: string[];
  score_payload?: {
    pi_factor_a?: number | null;
    pi_factor_b?: number | null;
    pi_factor_c?: number | null;
    pi_factor_d?: number | null;
  };
};

const Teams: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [teams, setTeams] = useState<Team[]>([]);
  const [internalPeopleCount, setInternalPeopleCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [importingReports, setImportingReports] = useState(false);
  const [syncingPeople, setSyncingPeople] = useState(false);
  const [unresolvedPlaceholders, setUnresolvedPlaceholders] = useState<Array<{ id: number; first_name: string; last_name: string }>>([]);
  const [placeholderEmails, setPlaceholderEmails] = useState<Record<number, string>>({});
  const [resolvingPlaceholders, setResolvingPlaceholders] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [manageRosterOpen, setManageRosterOpen] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', department: '', description: '' });
  const [creating, setCreating] = useState(false);

  const [rosterEmails, setRosterEmails] = useState<InternalTeamEmail[]>([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [newRosterFirstName, setNewRosterFirstName] = useState('');
  const [newRosterLastName, setNewRosterLastName] = useState('');
  const [newRosterEmail, setNewRosterEmail] = useState('');
  const [addingRosterEmail, setAddingRosterEmail] = useState(false);
  const [savingRosterEmailId, setSavingRosterEmailId] = useState<number | null>(null);
  const [removingRosterEmailId, setRemovingRosterEmailId] = useState<number | null>(null);
  const [rosterDrafts, setRosterDrafts] = useState<Record<number, { first_name: string; last_name: string; email: string }>>({});
  const [manualAssociateOpen, setManualAssociateOpen] = useState(false);
  const [manualAssociationRows, setManualAssociationRows] = useState<UnresolvedReportSample[]>([]);
  const [manualAssociationEmails, setManualAssociationEmails] = useState<Record<string, string>>({});
  const [manualAssociationOptions, setManualAssociationOptions] = useState<string[]>([]);
  const [loadingManualAssociationOptions, setLoadingManualAssociationOptions] = useState(false);
  const [savingManualAssociations, setSavingManualAssociations] = useState(false);
  const reportsInputRef = useRef<HTMLInputElement | null>(null);

  const fetchTeams = async () => {
    const response = await teamsApi.getAll();
    setTeams(response.data);
  };

  const fetchInternalPeopleCount = async () => {
    const response = await usersApi.getInternalPeople();
    setInternalPeopleCount((response.data || []).length);
  };

  const fetchRosterEmails = async () => {
    try {
      setLoadingRoster(true);
      const response = await usersApi.getInternalTeamEmails();
      const rows = sortRosterEntries(response.data?.emails || []);
      setRosterEmails(rows);
      setRosterDrafts(
        rows.reduce((acc, row) => {
          acc[row.id] = {
            first_name: row.first_name || '',
            last_name: row.last_name || '',
            email: row.email || '',
          };
          return acc;
        }, {} as Record<number, { first_name: string; last_name: string; email: string }>)
      );
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to load internal team email roster', 'error');
    } finally {
      setLoadingRoster(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        await Promise.all([
          fetchTeams(),
          fetchInternalPeopleCount(),
        ]);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load teams');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  const handleCreate = async () => {
    if (!newTeam.name.trim()) return;
    try {
      setCreating(true);
      const response = await teamsApi.create(newTeam);
      showNotification('Team created', 'success');
      setCreateOpen(false);
      setNewTeam({ name: '', department: '', description: '' });
      navigate(`/teams/${response.data.id}`);
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to create team', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenRosterManager = async () => {
    setManageRosterOpen(true);
    await fetchRosterEmails();
  };

  const handleOpenReportsPicker = () => {
    reportsInputRef.current?.click();
  };

  const handleSyncInternalPeople = async () => {
    try {
      setSyncingPeople(true);
      const response = await usersApi.syncInternalPeople();
      const { synced_count, error_count, unresolved_placeholders } = response.data;
      const msg = error_count > 0
        ? `Synced ${synced_count} people (${error_count} errors)`
        : `Synced ${synced_count} people from PI`;
      showNotification(msg, error_count > 0 ? 'warning' : 'success');
      await fetchInternalPeopleCount();

      if (unresolved_placeholders && unresolved_placeholders.length > 0) {
        setUnresolvedPlaceholders(unresolved_placeholders);
        setPlaceholderEmails(
          unresolved_placeholders.reduce((acc, p) => ({ ...acc, [p.id]: '' }), {} as Record<number, string>)
        );
      }
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to sync from PI', 'error');
    } finally {
      setSyncingPeople(false);
    }
  };

  const handleResolvePlaceholders = async () => {
    const resolutions = unresolvedPlaceholders
      .map((p) => ({ id: p.id, email: (placeholderEmails[p.id] || '').trim() }))
      .filter((r) => r.email && SIMPLE_EMAIL_REGEX.test(r.email));

    if (resolutions.length === 0) {
      showNotification('Enter at least one valid email to save', 'warning');
      return;
    }

    try {
      setResolvingPlaceholders(true);
      const response = await usersApi.resolvePlaceholders(resolutions);
      const succeeded = response.data.results.filter((r) => r.success).length;
      const failed = response.data.results.filter((r) => !r.success).length;
      showNotification(
        failed > 0 ? `Resolved ${succeeded}, failed ${failed}` : `Resolved ${succeeded} people`,
        failed > 0 ? 'warning' : 'success'
      );
      setUnresolvedPlaceholders([]);
      setPlaceholderEmails({});
      await fetchInternalPeopleCount();
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to resolve placeholders', 'error');
    } finally {
      setResolvingPlaceholders(false);
    }
  };
  const ensureManualAssociationOptions = async () => {
    if (manualAssociationOptions.length > 0) {
      return manualAssociationOptions;
    }
    try {
      setLoadingManualAssociationOptions(true);
      const [profilesResponse, rosterResponse] = await Promise.all([
        usersApi.getInternalProfiles(),
        usersApi.getInternalTeamEmails(),
      ]);
      const profileEmails = (profilesResponse.data || [])
        .map((profile: InternalProfile) => profile.email?.trim().toLowerCase())
        .filter((email: string | undefined): email is string => !!email && SIMPLE_EMAIL_REGEX.test(email));
      const rosterList = (rosterResponse.data?.emails || [])
        .map((entry) => entry.email?.trim().toLowerCase())
        .filter((email: string | undefined): email is string => !!email && SIMPLE_EMAIL_REGEX.test(email));
      const options = Array.from(new Set([...profileEmails, ...rosterList])).sort((a, b) => a.localeCompare(b));
      setManualAssociationOptions(options);
      return options;
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to load email options for manual report mapping', 'error');
      return [];
    } finally {
      setLoadingManualAssociationOptions(false);
    }
  };
  const handleManualAssociationEmailChange = (rowId: string, value: string) => {
    setManualAssociationEmails((prev) => ({
      ...prev,
      [rowId]: value.trim().toLowerCase(),
    }));
  };
  const handleSaveManualAssociations = async () => {
    const preparedAssociations = manualAssociationRows
      .map((row) => ({
        rowId: row.id,
        row,
        email: (manualAssociationEmails[row.id] || '').trim().toLowerCase(),
      }))
      .filter(({ row, email }) => (
        !!row.score_payload &&
        SIMPLE_EMAIL_REGEX.test(email) &&
        row.score_payload?.pi_factor_a != null &&
        row.score_payload?.pi_factor_b != null &&
        row.score_payload?.pi_factor_c != null &&
        row.score_payload?.pi_factor_d != null
      ))
      .map(({ rowId, row, email }) => ({
        rowId,
        filename: row.filename,
        email,
        parsed_name: row.parsed_name || null,
        score_payload: {
          pi_factor_a: Number(row.score_payload?.pi_factor_a),
          pi_factor_b: Number(row.score_payload?.pi_factor_b),
          pi_factor_c: Number(row.score_payload?.pi_factor_c),
          pi_factor_d: Number(row.score_payload?.pi_factor_d),
        },
      }));

    if (preparedAssociations.length === 0) {
      showNotification('Choose at least one valid email mapping before applying', 'error');
      return;
    }

    try {
      setSavingManualAssociations(true);
      const response = await usersApi.manualAssociateInternalReports(
        preparedAssociations.map(({ rowId, ...payload }) => payload)
      );
      const summary = response.data;
      const severity = summary.partial_failure ? 'warning' : 'success';
      showNotification(
        `${summary.message} (imported ${summary.imported_count}, unresolved ${summary.unresolved_count})`,
        severity
      );
      await fetchInternalPeopleCount();

      const mappedRowIds = new Set(preparedAssociations.map((entry) => entry.rowId));
      const remaining = manualAssociationRows.filter((row) => !mappedRowIds.has(row.id));
      setManualAssociationRows(remaining);
      setManualAssociationEmails((prev) => {
        const next = { ...prev };
        preparedAssociations.forEach((entry) => {
          delete next[entry.rowId];
        });
        return next;
      });
      if (remaining.length === 0) {
        setManualAssociateOpen(false);
      }
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to save manual report associations', 'error');
    } finally {
      setSavingManualAssociations(false);
    }
  };

  const handleImportReports = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const selectedFiles = Array.from(files);
    const zipFiles = selectedFiles.filter((file) => /\.zip$/i.test(file.name));
    const relevantReportFiles = selectedFiles.filter((file) =>
      /_(BehavioralReport|SelfConcept|ManagementSkillsGuide|PersonalDevelopmentGuide|CoachingGuide|PI_Insights)\.pdf$/i.test(file.name)
    );
    const behavioralReportFiles = selectedFiles.filter((file) => /_BehavioralReport\.pdf$/i.test(file.name));
    const pdfFiles = selectedFiles.filter((file) => /\.pdf$/i.test(file.name));

    let filesToUpload: File[] = [];
    if (zipFiles.length > 0) {
      filesToUpload = zipFiles;
    } else if (behavioralReportFiles.length > 0) {
      filesToUpload = behavioralReportFiles;
    } else if (relevantReportFiles.length > 0) {
      filesToUpload = relevantReportFiles;
    } else {
      filesToUpload = pdfFiles;
    }

    if (filesToUpload.length === 0) {
      showNotification('No ZIP or PDF files found in that selection', 'error');
      if (event.target) {
        event.target.value = '';
      }
      return;
    }

    try {
      setImportingReports(true);
      if (filesToUpload.length < selectedFiles.length) {
        showNotification(
          `Uploading ${filesToUpload.length} report file(s) from ${selectedFiles.length} selected`,
          'info'
        );
      }
      const response = await usersApi.importInternalReports(filesToUpload);
      const summary = response.data;
      const severity = summary.partial_failure ? 'warning' : 'success';
      showNotification(
        `${summary.message} (uploaded ${summary.uploaded_files}, pdfs ${summary.pdf_reports_found}, relevant ${summary.pdf_reports_relevant ?? summary.pdf_reports_found}, selected ${summary.pdf_reports_selected ?? summary.pdf_reports_found}, ignored ${summary.pdf_reports_ignored ?? 0}, imported ${summary.imported_count}, auto-created ${summary.auto_created_count ?? 0}, unresolved ${summary.unresolved_count})`,
        severity
      );
      await fetchInternalPeopleCount();
      await handleSyncInternalPeople();

      const unresolved = Array.isArray(summary.unresolved_samples)
        ? summary.unresolved_samples
        : [];
      const mappable = unresolved
        .filter((sample) => (
          !!sample?.score_payload &&
          (
            sample.reason === 'email_not_found_in_report' ||
            sample.reason === 'ambiguous_name_match' ||
            sample.reason === 'ambiguous_initial_last_match'
          )
        ))
        .map((sample, index) => ({
          id: `${sample.filename}__${index}`,
          filename: sample.filename,
          reason: sample.reason,
          parsed_name: sample.parsed_name || null,
          candidates: sample.candidates || [],
          suggested_emails: sample.suggested_emails || [],
          score_payload: sample.score_payload,
        })) as UnresolvedReportSample[];

      if (mappable.length > 0) {
        const optionEmails = await ensureManualAssociationOptions();
        const initialSelections = mappable.reduce((acc, row) => {
          const preferred = [
            ...(row.candidates || []),
            ...(row.suggested_emails || []),
          ].map((email) => String(email || '').trim().toLowerCase()).find((email) => SIMPLE_EMAIL_REGEX.test(email));
          const fallback = optionEmails.find((email) => (
            !!row.parsed_name &&
            email.replace(/[^a-z]/g, '').includes(String(row.parsed_name).replace(/\s+/g, ''))
          ));
          acc[row.id] = preferred || fallback || '';
          return acc;
        }, {} as Record<string, string>);

        setManualAssociationRows(mappable);
        setManualAssociationEmails(initialSelections);
        setManualAssociateOpen(true);
        showNotification(`Manual mapping available for ${mappable.length} unresolved report(s)`, 'warning');
      }
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to import internal report PDFs', 'error');
    } finally {
      setImportingReports(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleAddRosterEmail = async () => {
    const normalizedEmail = newRosterEmail.trim().toLowerCase();
    if (!normalizedEmail || !SIMPLE_EMAIL_REGEX.test(normalizedEmail)) {
      showNotification('Enter a valid email address', 'error');
      return;
    }
    const firstName = newRosterFirstName.trim();
    const lastName = newRosterLastName.trim();
    if (!firstName || !lastName) {
      showNotification('Enter first and last name', 'error');
      return;
    }

    try {
      setAddingRosterEmail(true);
      await usersApi.addInternalTeamEmail({
        email: normalizedEmail,
        first_name: firstName,
        last_name: lastName,
      });
      setNewRosterFirstName('');
      setNewRosterLastName('');
      setNewRosterEmail('');
      showNotification('Employee added to roster', 'success');
      await fetchRosterEmails();
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to add roster email', 'error');
    } finally {
      setAddingRosterEmail(false);
    }
  };

  const handleRemoveRosterEmail = async (emailId: number) => {
    try {
      setRemovingRosterEmailId(emailId);
      await usersApi.removeInternalTeamEmail(emailId);
      showNotification('Email removed from roster', 'success');
      await fetchRosterEmails();
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to remove roster email', 'error');
    } finally {
      setRemovingRosterEmailId(null);
    }
  };

  const handleRosterDraftChange = (
    emailId: number,
    field: 'first_name' | 'last_name' | 'email',
    value: string
  ) => {
    const currentRow = rosterEmails.find((entry) => entry.id === emailId);
    setRosterDrafts((prev) => ({
      ...prev,
      [emailId]: {
        first_name: prev[emailId]?.first_name ?? currentRow?.first_name ?? '',
        last_name: prev[emailId]?.last_name ?? currentRow?.last_name ?? '',
        email: prev[emailId]?.email ?? currentRow?.email ?? '',
        [field]: value,
      },
    }));
  };

  const handleSaveRosterEmail = async (emailId: number) => {
    const currentRow = rosterEmails.find((entry) => entry.id === emailId);
    const draft = rosterDrafts[emailId] || (currentRow ? {
      first_name: currentRow.first_name || '',
      last_name: currentRow.last_name || '',
      email: currentRow.email || '',
    } : null);
    if (!draft) return;

    const firstName = draft.first_name.trim();
    const lastName = draft.last_name.trim();
    const normalizedEmail = draft.email.trim().toLowerCase();
    if (!firstName || !lastName) {
      showNotification('First and last name are required', 'error');
      return;
    }
    if (!normalizedEmail || !SIMPLE_EMAIL_REGEX.test(normalizedEmail)) {
      showNotification('Enter a valid email address', 'error');
      return;
    }

    try {
      setSavingRosterEmailId(emailId);
      const response = await usersApi.updateInternalTeamEmail(emailId, {
        first_name: firstName,
        last_name: lastName,
        email: normalizedEmail,
      });
      const updatedEntry = response.data?.email;
      if (updatedEntry?.id) {
        setRosterEmails((prev) => sortRosterEntries(
          prev.map((entry) => (entry.id === updatedEntry.id ? updatedEntry : entry))
        ));
        setRosterDrafts((prev) => ({
          ...prev,
          [updatedEntry.id]: {
            first_name: updatedEntry.first_name || '',
            last_name: updatedEntry.last_name || '',
            email: updatedEntry.email || '',
          },
        }));
      }
      showNotification('Roster entry updated', 'success');
      await fetchInternalPeopleCount();
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Failed to update roster entry', 'error');
    } finally {
      setSavingRosterEmailId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const manualReasonLabel: Record<string, string> = {
    email_not_found_in_report: 'No email could be auto-matched',
    ambiguous_name_match: 'Multiple exact name matches found',
    ambiguous_initial_last_match: 'Multiple initial+last matches found',
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
        <Typography variant="h4">Teams</Typography>
        <Stack direction="row" spacing={1}>
          <input
            ref={reportsInputRef}
            type="file"
            multiple
            accept=".zip,.pdf,application/zip,application/pdf"
            style={{ display: 'none' }}
            onChange={handleImportReports}
          />
          <Button variant="outlined" onClick={handleOpenRosterManager}>
            Manage Employee Roster
          </Button>
          <Button
            variant="outlined"
            startIcon={syncingPeople ? <CircularProgress size={16} color="inherit" /> : <Sync />}
            onClick={handleSyncInternalPeople}
            disabled={syncingPeople || importingReports}
          >
            {syncingPeople ? 'Syncing...' : 'Sync from PI'}
          </Button>
          <Button
            variant="outlined"
            startIcon={importingReports ? <CircularProgress size={16} color="inherit" /> : <UploadFile />}
            onClick={handleOpenReportsPicker}
            disabled={importingReports || syncingPeople}
          >
            {importingReports ? 'Importing...' : 'Import Reports ZIP/PDF'}
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            New Team
          </Button>
        </Stack>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Internal People Catalog
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Upload PI report ZIP/PDF files here to maintain one shared internal people catalog for all teams.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip label={`${internalPeopleCount} internal people available`} color="primary" variant="outlined" />
          <Chip label={`${teams.length} team${teams.length === 1 ? '' : 's'}`} variant="outlined" />
        </Stack>
      </Paper>

      {teams.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Groups sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No teams yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a team and add members from your internal people catalog.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Create Your First Team
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
          {teams.map((team) => (
            <Card key={team.id}>
              <CardActionArea onClick={() => navigate(`/teams/${team.id}`)}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Groups color="primary" />
                    <Typography variant="h6">{team.name}</Typography>
                  </Box>
                  {team.department && (
                    <Chip label={team.department} size="small" variant="outlined" sx={{ mb: 1 }} />
                  )}
                  {team.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {team.description}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {team.member_count || 0} member{(team.member_count || 0) !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={manageRosterOpen} onClose={() => setManageRosterOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Manage Employee Roster</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Maintain the master employee roster here. Add and edit employee names and emails used for report imports and manual mappings.
            </Typography>

            <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
              <TextField
                label="First name"
                size="small"
                value={newRosterFirstName}
                onChange={(e) => setNewRosterFirstName(e.target.value)}
                sx={{ flex: '1 1 180px' }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddRosterEmail();
                  }
                }}
              />
              <TextField
                label="Last name"
                size="small"
                value={newRosterLastName}
                onChange={(e) => setNewRosterLastName(e.target.value)}
                sx={{ flex: '1 1 180px' }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddRosterEmail();
                  }
                }}
              />
              <TextField
                label="Email"
                size="small"
                value={newRosterEmail}
                onChange={(e) => setNewRosterEmail(e.target.value)}
                sx={{ flex: '1 1 300px' }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddRosterEmail();
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={handleAddRosterEmail}
                disabled={
                  addingRosterEmail ||
                  !newRosterEmail.trim() ||
                  !newRosterFirstName.trim() ||
                  !newRosterLastName.trim()
                }
              >
                {addingRosterEmail ? 'Adding...' : 'Add'}
              </Button>
            </Box>

            {loadingRoster ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            ) : rosterEmails.length === 0 ? (
              <Alert severity="warning">No roster employees configured yet.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>First Name</TableCell>
                      <TableCell>Last Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell width={220}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rosterEmails.map((entry) => {
                      const draft = rosterDrafts[entry.id] || {
                        first_name: entry.first_name || '',
                        last_name: entry.last_name || '',
                        email: entry.email || '',
                      };
                      const isSaving = savingRosterEmailId === entry.id;
                      const isRemoving = removingRosterEmailId === entry.id;
                      return (
                        <TableRow key={entry.id}>
                          <TableCell>
                            <TextField
                              size="small"
                              value={draft.first_name}
                              onChange={(event) => handleRosterDraftChange(entry.id, 'first_name', event.target.value)}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={draft.last_name}
                              onChange={(event) => handleRosterDraftChange(entry.id, 'last_name', event.target.value)}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={draft.email}
                              onChange={(event) => handleRosterDraftChange(entry.id, 'email', event.target.value)}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSaveRosterEmail(entry.id)}
                                disabled={isSaving || isRemoving}
                              >
                                {isSaving ? 'Saving...' : 'Save'}
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => handleRemoveRosterEmail(entry.id)}
                                disabled={isSaving || isRemoving}
                              >
                                {isRemoving ? 'Removing...' : 'Remove'}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fetchRosterEmails} disabled={loadingRoster}>Refresh</Button>
          <Button onClick={() => setManageRosterOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manualAssociateOpen} onClose={() => setManualAssociateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Map Unresolved Reports</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Auto-match could not resolve some reports. Pick the correct employee email for each report and apply mappings.
            </Typography>

            {loadingManualAssociationOptions && (
              <Box display="flex" justifyContent="center" py={1}>
                <CircularProgress size={20} />
              </Box>
            )}

            <Stack spacing={1.5} sx={{ maxHeight: '55vh', overflowY: 'auto', pr: 0.5 }}>
              {manualAssociationRows.map((row) => {
                const emailOptions = Array.from(new Set([
                  ...(row.candidates || []),
                  ...(row.suggested_emails || []),
                  ...manualAssociationOptions,
                ]))
                  .map((email) => String(email || '').trim().toLowerCase())
                  .filter((email) => SIMPLE_EMAIL_REGEX.test(email));
                return (
                  <Paper key={row.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2">{row.filename}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {manualReasonLabel[row.reason] || row.reason}
                      {row.parsed_name ? ` • parsed name: ${row.parsed_name}` : ''}
                    </Typography>
                    {row.score_payload && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        A {row.score_payload.pi_factor_a} • B {row.score_payload.pi_factor_b} • C {row.score_payload.pi_factor_c} • D {row.score_payload.pi_factor_d}
                      </Typography>
                    )}
                    <TextField
                      size="small"
                      fullWidth
                      label="Associate to email"
                      value={manualAssociationEmails[row.id] || ''}
                      onChange={(event) => handleManualAssociationEmailChange(row.id, event.target.value)}
                      inputProps={{ list: `manual-email-options-${row.id}` }}
                      helperText="Pick from suggestions or type an email"
                    />
                    <datalist id={`manual-email-options-${row.id}`}>
                      {emailOptions.map((email) => (
                        <option key={`${row.id}-${email}`} value={email} />
                      ))}
                    </datalist>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualAssociateOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={handleSaveManualAssociations}
            disabled={savingManualAssociations || manualAssociationRows.length === 0}
          >
            {savingManualAssociations ? 'Applying...' : 'Apply Mappings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Team Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Team</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Team Name"
              value={newTeam.name}
              onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Department"
              value={newTeam.department}
              onChange={(e) => setNewTeam({ ...newTeam, department: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={newTeam.description}
              onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating || !newTeam.name.trim()}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unresolvedPlaceholders.length > 0} onClose={() => setUnresolvedPlaceholders([])} maxWidth="sm" fullWidth>
        <DialogTitle>Missing Emails</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These people were imported from PI reports but their email addresses couldn't be determined automatically. Enter their correct emails to link them to their PI accounts.
          </Typography>
          <Stack spacing={2}>
            {unresolvedPlaceholders.map((p) => (
              <Box key={p.id}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                  {p.first_name} {p.last_name}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="email@company.com"
                  value={placeholderEmails[p.id] || ''}
                  onChange={(e) => setPlaceholderEmails((prev) => ({ ...prev, [p.id]: e.target.value }))}
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnresolvedPlaceholders([])}>Skip for now</Button>
          <Button
            variant="contained"
            onClick={handleResolvePlaceholders}
            disabled={resolvingPlaceholders}
          >
            {resolvingPlaceholders ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Teams;
